import React, { useState, useEffect, useCallback, useMemo } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useNotification } from "Containers/Notifications";
import { Button } from "ds/Button";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { isDifferent } from "Libs/difference";
import { setDeep } from "Libs/objectAccess";
import { gitProjectSelector } from "Reducers/project/project";
import {
  envDataRetentionSelector,
  projectSettingsLoadingSelector,
  projectSettingsErrorSelector,
  updateBackupSchedule
} from "Reducers/projectSettings";
import Logo from "src/common/containers/NavBar/Logo/Logo";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import BackupRow from "./components/BackupRow";
import MaxManualBackupsInput from "./components/MaxManualBackupsInput";
import * as S from "./ManageBackupSchedulePage.styles";
import {
  backupIntervalRegulator,
  sortBackSchedule,
  removeDuplicateInterval,
  findSchedulesThatDoNotHaveMinimalInterval
} from "./util";

import type {
  EnvTypeDataRetention,
  BackupDefaultConfig
} from "Reducers/projectSettings";

export type ManageBackupEnvironmentOption = {
  value: EnvTypeDataRetention;
  label: string;
};
export type IntervalOptionsType = { label: string; id: string };
export type IntervalNumberOptionsType = { label: string; id: number };

const environmentOptions: ManageBackupEnvironmentOption[] = [
  { label: "Production", value: "production" },
  { label: "Staging", value: "staging" },
  { label: "Development", value: "development" }
];

const intervalOptions: IntervalOptionsType[] = [
  { label: "Hour", id: "h" },
  { label: "Day", id: "d" },
  { label: "Week", id: "w" },
  { label: "Month", id: "M" },
  { label: "Year", id: "y" }
];

const intervalNumberOptions: IntervalNumberOptionsType[] = [
  { label: "1", id: 1 },
  { label: "2", id: 2 },
  { label: "3", id: 3 },
  { label: "4", id: 4 },
  { label: "6", id: 6 },
  { label: "8", id: 8 },
  { label: "12", id: 12 }
];

const defaultBackupsByEnvType: Record<string, BackupDefaultConfig> = {
  production: {
    max_backups: -1,
    default_config: {
      manual_count: 2,
      schedule: [
        {
          interval: "1d",
          count: 2,
          uuid: "16ce"
        },
        {
          interval: "",
          count: undefined,
          uuid: `4d01`
        }
      ]
    }
  },
  staging: {
    max_backups: -1,
    default_config: {
      manual_count: 1,
      schedule: [
        {
          interval: "",
          count: undefined,
          uuid: `885b`
        }
      ]
    }
  },
  development: {
    max_backups: -1,
    default_config: {
      manual_count: 1,
      schedule: [
        {
          interval: "",
          count: undefined,
          uuid: `a35c`
        }
      ]
    }
  }
};

const ManageBackupSchedulePage = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const notify = useNotification();
  const dispatch = useAppDispatch();

  const { projectId, environmentType } = useDecodedParams<{
    projectId: string;
    environmentType: EnvTypeDataRetention;
  }>();

  const [checkedAgreement, setCheckedAgreement] = useState(false);
  const [backupRetentionReference, setBackupRetentionReference] =
    useState<BackupDefaultConfig>();
  const [backupRetention, setBackupRetention] = useState<BackupDefaultConfig>();
  const [totalBackupsStored, setTotalBackupsStored] = useState<number>(0);

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const backupRetentionGlobalState = useSelectorWithUrlParams(
    envDataRetentionSelector
  );
  const isLoading = useAppSelector(projectSettingsLoadingSelector);
  const projectSettingsError = useAppSelector(projectSettingsErrorSelector);

  const [backupScheduleSettingsUrl] = useUrls([
    "organization.project.settings.backupSchedules"
  ]);

  const onExit = () => {
    navigate(backupScheduleSettingsUrl);
  };

  const defaultEnvironmentSelection = environmentOptions.find(
    env => env.value === environmentType
  ) ?? { value: "", label: "" };

  const defaultBackupValuesByEnvType = defaultBackupsByEnvType[environmentType];

  const handleManualBackupChange = (newValue: number) => {
    setBackupRetention(oldData => {
      const updatedData = { ...oldData };

      setDeep(updatedData, ["default_config", "manual_count"], newValue);

      return updatedData;
    });
  };

  const handleDeleteRow = (index: number) => {
    setBackupRetention(oldData => {
      const updatedData = { ...oldData };

      if (updatedData?.default_config?.schedule) {
        updatedData.default_config.schedule =
          updatedData.default_config.schedule.filter((_, i) => i !== index);
      }

      return updatedData;
    });
  };

  const handleIntervalChange = (
    interval: string,
    index: number,
    count: number
  ) => {
    setBackupRetention(oldData => {
      const updatedData = { ...oldData };

      if (
        typeof updatedData?.default_config?.schedule?.[index]?.interval ===
        "string"
      ) {
        updatedData.default_config.schedule[index].interval = interval;
        updatedData.default_config.schedule[index].count = count;
      }

      return updatedData;
    });
  };

  const handleBackupsCountChange = (newNumber: number, index: number) => {
    setBackupRetention(oldData => {
      const updatedData = { ...oldData };
      updatedData.default_config!.schedule[index].count = newNumber;

      return updatedData;
    });
  };

  const calculateTotalBackupsStored = backupRetentionData => {
    let storedBackups = 0;

    storedBackups += backupRetentionData?.default_config?.manual_count ?? 0;

    backupRetentionData?.default_config?.schedule.forEach(
      schedule => (storedBackups += schedule?.count ?? 0)
    );

    setTotalBackupsStored(storedBackups);
  };

  const createNewBlankRow = () => ({
    interval: "",
    count: undefined,
    uuid: `${Math.pow(Math.random(), Math.random())}`
  });

  const createNewBlankDataRetention = () => ({
    max_backups: -1,
    default_config: {
      manual_count: 1,
      schedule: []
    }
  });

  const init = useCallback(
    (defaultBackup?: BackupDefaultConfig) => {
      /**
       * defaultBackup - for reverting the rows to the original default
       * backupRetentionGlobalState - for the data that comes from the api
       * createNewBlankDataRetention - for staging since the staging api value isn't given, an object shape has to be provided
       */
      const copiedBackupRetention = structuredClone(
        defaultBackup ??
          backupRetentionGlobalState ??
          createNewBlankDataRetention()
      );

      if (!defaultBackup) {
        if (typeof copiedBackupRetention?.max_backups === "number") {
          copiedBackupRetention.max_backups = -1;
        }

        copiedBackupRetention?.default_config?.schedule?.push(
          createNewBlankRow()
        );

        copiedBackupRetention?.default_config?.schedule.forEach(
          schedule =>
            (schedule.uuid = `${Math.pow(Math.random(), Math.random())}`)
        );
      }

      const copiedBackupRetentionReference = structuredClone(
        copiedBackupRetention
      );

      setBackupRetention(copiedBackupRetention);
      setBackupRetentionReference(copiedBackupRetentionReference);
    },
    [backupRetentionGlobalState]
  );

  const areAllRowsAreFilled = useCallback(() => {
    let allRowsAreFilled = true;

    backupRetention?.default_config?.schedule.forEach(schedule => {
      allRowsAreFilled =
        allRowsAreFilled && schedule.count! > 0 && !!schedule.interval;
    });

    return allRowsAreFilled;
  }, [backupRetention?.default_config?.schedule]);

  const insertNewBlankRow = useCallback(() => {
    setBackupRetention(prevState => {
      const copyBackupRetention = { ...prevState };

      copyBackupRetention.default_config?.schedule.push(createNewBlankRow());

      return copyBackupRetention;
    });
  }, []);

  useEffect(() => {
    if (!isLoading) {
      init();
    }
  }, [isLoading, init]);

  useEffect(() => {
    if (!isLoading && backupRetentionReference && backupRetention) {
      calculateTotalBackupsStored(backupRetention);
    }

    const rowsAreFilled = areAllRowsAreFilled();
    if (!isLoading && backupRetention && rowsAreFilled) {
      insertNewBlankRow();
    }
  }, [
    isLoading,
    backupRetention,
    backupRetentionReference,
    areAllRowsAreFilled,
    insertNewBlankRow
  ]);

  const schedulesWithoutMinimalInterval =
    findSchedulesThatDoNotHaveMinimalInterval(
      backupRetention?.default_config?.schedule ?? []
    );

  const schedule = backupRetention?.default_config?.schedule;
  const lastSchedule = schedule?.[schedule.length - 1];

  const lastRowIsIncomplete =
    lastSchedule?.count! > 0 || !!lastSchedule?.interval;

  const hasChangedFromDefaultValues = useMemo(() => {
    if (!defaultBackupValuesByEnvType || !backupRetention) return false;

    const copyDefaultBackupValuesByEnvType = structuredClone(
      defaultBackupValuesByEnvType
    );
    copyDefaultBackupValuesByEnvType?.default_config?.schedule.pop();
    copyDefaultBackupValuesByEnvType?.default_config?.schedule.forEach(
      schedule => {
        if (schedule?.uuid) {
          delete schedule.uuid;
        }
      }
    );

    const copyBackupRetention = structuredClone(backupRetention);
    copyBackupRetention?.default_config?.schedule.pop();
    copyBackupRetention?.default_config?.schedule.forEach(schedule => {
      if (schedule?.uuid) {
        delete schedule.uuid;
      }
    });

    return isDifferent(copyDefaultBackupValuesByEnvType, copyBackupRetention);
  }, [defaultBackupValuesByEnvType, backupRetention]);

  const hasChangedFromApiValues = useMemo(() => {
    if (!backupRetention) return false;

    const copyBackupRetentionGlobalState = structuredClone(
      backupRetentionGlobalState
    );

    const copyBackupRetention = structuredClone(backupRetention);
    copyBackupRetention?.default_config?.schedule.pop();
    copyBackupRetention?.default_config?.schedule.forEach(schedule => {
      if (schedule?.uuid) {
        delete schedule.uuid;
      }
    });

    return isDifferent(
      copyBackupRetentionGlobalState ?? {},
      copyBackupRetention
    );
  }, [backupRetentionGlobalState, backupRetention]);

  const saveBtnIsEnabled =
    checkedAgreement &&
    hasChangedFromApiValues &&
    !lastRowIsIncomplete &&
    !schedulesWithoutMinimalInterval.length;

  const onSubmit = async () => {
    if (saveBtnIsEnabled) {
      const copiedBackupRetention = structuredClone(backupRetention);

      // Last row will always be an empty row
      copiedBackupRetention?.default_config?.schedule.pop();

      // uuid was added to help react correctly render data on changes
      // Now that the data is ready to be saved, clean the data
      copiedBackupRetention?.default_config?.schedule.forEach(schedule => {
        if (schedule?.uuid) {
          delete schedule.uuid;
        }
      });

      /**
       * Error will be thrown if the schedule
       * --> Is not sorted from shortest to longest time
       * --> Has duplicate interval lengths
       */
      sortBackSchedule(copiedBackupRetention!.default_config!.schedule);
      copiedBackupRetention!.default_config!.schedule = removeDuplicateInterval(
        copiedBackupRetention!.default_config!.schedule
      );

      const result = await dispatch(
        updateBackupSchedule({
          backupRetentionData: copiedBackupRetention!,
          projectId,
          environmentType
        })
      );

      if (result?.type?.includes("fulfilled")) {
        notify.success({
          title: intl.formatMessage({
            id: "backup_schedule.notify.title",
            defaultMessage: "Backup schedule has been updated"
          }),
          description: intl.formatMessage(
            {
              id: "backup_schedule.notify.description"
            },
            {
              environmentType: defaultEnvironmentSelection?.label
            }
          )
        });
        onExit();
      }
    }
  };

  return (
    <S.Form>
      {isLoading ? (
        <S.Loading iconOnly={true} />
      ) : (
        <S.ScrollContainer>
          <S.VendorIconWrapper>
            <Button to="/">
              <Logo isFullLogo />
            </Button>
          </S.VendorIconWrapper>
          <S.ContentWrapper>
            <S.ProjectWrapper>
              <Typography.Label>{project?.title ?? ""}</Typography.Label>
            </S.ProjectWrapper>
            <S.ManageBackupTitle tag="h1">
              {intl.formatMessage({
                id: "backup_schedule.title",
                defaultMessage: "Manage backup schedule"
              })}
            </S.ManageBackupTitle>
            <S.ManageBackupDescription>
              {intl.formatMessage<React.ReactNode>(
                { id: "backup_schedule.description" },
                {
                  link: chunks => (
                    <S.ReadDocLink
                      variant="link"
                      analyticId="links.documentation.environments.backup"
                      href={intl.formatMessage({
                        id: "links.documentation.environments.backup"
                      })}
                    >
                      {chunks}
                    </S.ReadDocLink>
                  )
                }
              )}
            </S.ManageBackupDescription>
            <S.EnvironmentWrapper>
              <S.EnvTypeWrapper>
                <S.LabelInput htmlFor="environment_type">
                  {intl.formatMessage({
                    id: "backup_schedule.environment_type.label",
                    defaultMessage: "Environment type"
                  })}
                </S.LabelInput>
                <Typography.BodyText>
                  {defaultEnvironmentSelection.label}
                </Typography.BodyText>
              </S.EnvTypeWrapper>

              <S.MaxBackupWrapper>
                <MaxManualBackupsInput
                  value={backupRetention?.default_config!.manual_count ?? 0}
                  manualBackupOnChange={newValue => {
                    handleManualBackupChange(newValue);
                  }}
                />
              </S.MaxBackupWrapper>
              <span />
            </S.EnvironmentWrapper>
          </S.ContentWrapper>
          <S.ContentWrapper>
            <S.HeadRow>
              <span />
              <span />
              <Typography.BodyText
                $color="granite"
                weight="semiBold"
                size="small"
              >
                {intl.formatMessage({
                  id: "backup_schedule.column.interval.label",
                  defaultMessage: "Backup interval"
                })}
              </Typography.BodyText>
              <span />
              <S.BackupsColLabel
                $color="granite"
                weight="semiBold"
                size="small"
              >
                {intl.formatMessage({
                  id: "backup_schedule.column.backups.label",
                  defaultMessage: "#Backups"
                })}
              </S.BackupsColLabel>
            </S.HeadRow>
            <S.Line />
            {backupRetention?.default_config?.schedule?.map(
              ({ count, interval, uuid }, i, origArr) => {
                const intervalCount = interval
                  ? +interval.replace(/\D*/g, "")
                  : undefined;

                const intervalLabel = interval.replace(/\d*/g, "");

                const intervalSelection = intervalOptions.find(
                  option => option.id === intervalLabel
                );

                const notMultipleOfMinInterval =
                  schedulesWithoutMinimalInterval.find(
                    sch => sch.uuid === uuid
                  );

                return (
                  <React.Fragment key={uuid}>
                    <BackupRow
                      rowNumber={i + 1}
                      deleteRow={() => {
                        handleDeleteRow(i);
                      }}
                      showIncrementError={!!notMultipleOfMinInterval}
                      isLastRow={origArr.length - 1 === i}
                      // Interval Count
                      intervalCount={intervalCount}
                      onIntervalCountChange={newNumber => {
                        handleIntervalChange(
                          `${newNumber}${intervalLabel}`,
                          i,
                          count || 1
                        );
                      }}
                      onIntervalCountBlur={newNumber => {
                        const labelInterval =
                          intervalLabel || intervalOptions[0].id;
                        const regulatedIntervalNumber = backupIntervalRegulator(
                          newNumber,
                          labelInterval
                        );

                        handleIntervalChange(
                          `${regulatedIntervalNumber}${labelInterval}`,
                          i,
                          count || 1
                        );
                      }}
                      intervalNumberOptions={intervalNumberOptions}
                      // Interval Select
                      intervalLabel={intervalLabel}
                      onIntervalLabelChange={option => {
                        const regulatedIntervalNumber = backupIntervalRegulator(
                          intervalCount ?? 0,
                          option.id
                        );

                        handleIntervalChange(
                          `${regulatedIntervalNumber || 1}${option.id}`,
                          i,
                          count || 1
                        );
                      }}
                      intervalOptions={intervalOptions}
                      intervalSelection={intervalSelection}
                      // Backup Count
                      backupsCount={count}
                      onBackupsCountChange={newNumber => {
                        handleBackupsCountChange(newNumber, i);
                      }}
                    />
                    <S.Line />
                  </React.Fragment>
                );
              }
            )}
          </S.ContentWrapper>
          <S.ContentWrapper>
            <S.TotalWrapper>
              <S.TotalBackup size="small" weight="semiBold">
                {intl.formatMessage({
                  id: "backup_schedule.column.stored.label",
                  defaultMessage: "Total backups stored"
                })}
              </S.TotalBackup>
              <span />
              <span />
              <span />
              <S.TotalBackup size="small" weight="semiBold">
                {totalBackupsStored}
              </S.TotalBackup>
              <span />
            </S.TotalWrapper>
            <S.RevertBtn
              analyticId="backup_schedule.btn.revert"
              variant="outline"
              onClick={() => {
                init(defaultBackupValuesByEnvType);
              }}
              disabled={!hasChangedFromDefaultValues}
            >
              {intl.formatMessage({
                id: "backup_schedule.btn.revert",
                defaultMessage: "Revert to default"
              })}
            </S.RevertBtn>

            {projectSettingsError && (
              <S.StickerRoot priority="critical">
                <Sticker.Icon priority="critical" />
                <Sticker.Text>{projectSettingsError}</Sticker.Text>
              </S.StickerRoot>
            )}

            <S.Checkbox
              id="backup_schedule_agreement"
              forId="backup_schedule_agreement"
              label={
                <>
                  {intl.formatMessage({
                    id: "backup_schedule.agreement",
                    defaultMessage:
                      "I understand that changes made will be applied to all environments of this type, and reducing the number of backups could delete older backups."
                  })}
                </>
              }
              checked={checkedAgreement}
              onCheckedChanged={value => {
                setCheckedAgreement(value);
              }}
            />

            <S.Note>
              {intl.formatMessage<React.ReactNode>(
                { id: "backup_schedule.note" },
                {
                  link: chunk => (
                    <S.ViewPricingBtn
                      variant="link"
                      analyticId="links.pricing"
                      href={intl.formatMessage({ id: "links.pricing" })}
                    >
                      {chunk}
                    </S.ViewPricingBtn>
                  )
                }
              )}
            </S.Note>
            <S.BtnWrapper>
              <Button variant="secondary" onClick={onExit}>
                {intl.formatMessage({
                  id: "cancel"
                })}
              </Button>
              <Button onClick={onSubmit} disabled={!saveBtnIsEnabled}>
                {intl.formatMessage({
                  id: "save"
                })}
              </Button>
            </S.BtnWrapper>
          </S.ContentWrapper>
        </S.ScrollContainer>
      )}
    </S.Form>
  );
};

export default ManageBackupSchedulePage;
